import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { CartProvider, useCart } from './context/CartContext';
import Navbar from './components/Navbar';
import CartDrawer from './components/CartDrawer';
import CartFloatingBar from './components/CartFloatingBar';
import Footer from './components/Footer';
import BackgroundPattern from './components/BackgroundPattern';
import AnalyticsCollector from './components/AnalyticsCollector';
import { LegacySelectVariantRedirect, LegacyChooseCategoryRedirect } from './components/LegacyRedirects';
import Home from './pages/Home';
import About from './pages/About';
import SelectVariant from './pages/SelectVariant';
import ChooseCategory from './pages/ChooseCategory';
import SpareParts from './pages/SpareParts';
import Login from './pages/admin/Login';
import Dashboard from './pages/admin/Dashboard';
import AdminLayout from './pages/admin/AdminLayout';
import CarModels from './pages/admin/CarModels';
import CarVariants from './pages/admin/CarVariants';
import SparePartsAdmin from './pages/admin/SpareParts';
import Settings from './pages/admin/Settings';
import Analytics from './pages/admin/Analytics';
import Orders from './pages/admin/Orders';
import './styles/global.css';
import './App.css';

function ScrollToTop() {
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    if (typeof window.history.scrollRestoration === 'string') {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      prevPathRef.current = location.pathname;
      window.scrollTo(0, 0);
      const rafId = requestAnimationFrame(() => {
        window.scrollTo(0, 0);
      });
      const timeoutId = setTimeout(() => window.scrollTo(0, 0), 100);
      return () => {
        cancelAnimationFrame(rafId);
        clearTimeout(timeoutId);
      };
    }
  }, [location.pathname]);

  return null;
}

function AppContent() {
  const location = useLocation();
  const { totalCount } = useCart();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [cartOpen, setCartOpen] = useState(false);
  const isAdminRoute = location.pathname.startsWith('/admin');

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    setIsAuthenticated(!!token);
    setCheckingAuth(false);
  }, []);

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const PrivateRoute = ({ children }) => {
    if (checkingAuth) {
      return <div className="spinner"></div>;
    }
    return isAuthenticated ? children : <Navigate to="/admin/login" />;
  };

  return (
    <div className="App">
      <ScrollToTop />
      <AnalyticsCollector />
      {!isAdminRoute && <BackgroundPattern />}
      {!isAdminRoute && <Navbar onOpenCart={() => setCartOpen(true)} />}
      {!isAdminRoute && <CartDrawer isOpen={cartOpen} onClose={() => setCartOpen(false)} />}
      {!isAdminRoute && <CartFloatingBar onOpenCart={() => setCartOpen(true)} />}
      <main className={`main-content ${!isAdminRoute && totalCount > 0 ? 'main-content--cart-bar' : ''}`}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/models/:slug" element={<SelectVariant />} />
          <Route path="/cars/:slug" element={<ChooseCategory />} />
          <Route path="/select-variant/:legacyId" element={<LegacySelectVariantRedirect />} />
          <Route path="/choose-category/:legacyId" element={<LegacyChooseCategoryRedirect />} />
          <Route path="/car-viewer/:legacyId" element={<LegacyChooseCategoryRedirect />} />
          <Route path="/parts/:area" element={<SpareParts />} />
          <Route
            path="/admin/login"
            element={
              isAuthenticated ? <Navigate to="/admin/dashboard" /> : <Login onLogin={handleLogin} />
            }
          />
          <Route
            path="/admin/dashboard"
            element={
              <PrivateRoute>
                <AdminLayout>
                  <Dashboard />
                </AdminLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/orders/:id"
            element={
              <PrivateRoute>
                <AdminLayout>
                  <Orders />
                </AdminLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/orders"
            element={
              <PrivateRoute>
                <AdminLayout>
                  <Orders />
                </AdminLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/car-models"
            element={
              <PrivateRoute>
                <AdminLayout>
                  <CarModels />
                </AdminLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/car-variants"
            element={
              <PrivateRoute>
                <AdminLayout>
                  <CarVariants />
                </AdminLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/spare-parts"
            element={
              <PrivateRoute>
                <AdminLayout>
                  <SparePartsAdmin />
                </AdminLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <PrivateRoute>
                <AdminLayout>
                  <Settings />
                </AdminLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/analytics"
            element={
              <PrivateRoute>
                <AdminLayout>
                  <Analytics />
                </AdminLayout>
              </PrivateRoute>
            }
          />
          <Route path="/admin/*" element={<Navigate to="/admin/login" />} />
        </Routes>
      </main>
      {!isAdminRoute && <Footer />}
    </div>
  );
}

function App() {
  return (
    <Router>
      <CartProvider>
        <AppContent />
      </CartProvider>
    </Router>
  );
}

export default App;
